/**
 * Where a person may be sent back to after signing in (LH-1508). One rule for the
 * client, which asks for the path, and the server, which carries it through the
 * OAuth state and redirects to it.
 */

const BASE = 'https://portal.invalid';

/**
 * Path as the router matches it: decoded, dot segments resolved, case-folded. Null when
 * it cannot be read that way (a decoded backslash or control character, or no valid URL):
 * such a path is never a place to send anyone. Never throws.
 */
function routeKey(pathname: string): string | null {
  let decoded = pathname;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    // Malformed escapes: compare the raw form.
  }
  // eslint-disable-next-line no-control-regex
  if (decoded.includes('\\') || /[\u0000-\u001f\u007f]/.test(decoded)) return null;
  try {
    return new URL(decoded.replace(/\/{2,}/g, '/'), BASE).pathname.toLowerCase().replace(/\/+$/, '') || '/';
  } catch {
    return null;
  }
}

function isSignInKey(key: string): boolean {
  return key === '/login' || key.startsWith('/login/') || key === '/auth' || key.startsWith('/auth/');
}

/** True for the sign-in page and the auth routes, however the path is spelled. */
export function isSignInPath(pathname: string): boolean {
  const key = routeKey(pathname);
  return key !== null && isSignInKey(key);
}

/**
 * A same-site path (with its query and fragment) to return to, normalized; anything
 * else, including the sign-in routes themselves, is '/'.
 */
export function safeReturnPath(target: unknown): string {
  if (typeof target !== 'string' || target.length > 2048) return '/';
  // eslint-disable-next-line no-control-regex
  if (!target.startsWith('/') || target.startsWith('//') || target.includes('\\') || /[\u0000-\u001f\u007f]/.test(target)) return '/';
  let url: URL;
  try {
    url = new URL(target, BASE);
  } catch {
    return '/';
  }
  const key = routeKey(url.pathname);
  if (url.origin !== BASE || key === null || isSignInKey(key)) return '/';
  const path = `${url.pathname}${url.search}${url.hash}`;
  return path.startsWith('//') ? '/' : path;
}

/** The return path with `_auth=1` in its query, before any fragment, so the client re-reads who is signed in. */
export function withAuthMarker(target: string): string {
  const url = new URL(safeReturnPath(target), BASE);
  url.searchParams.set('_auth', '1');
  return `${url.pathname}${url.search}${url.hash}`;
}
